import React, { useMemo, useState, useEffect } from "react";
import {
  Button,
  Card,
  Dropdown,
  FormCheck,
  Modal,
  Spinner,
} from "react-bootstrap";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faThLarge,
  faRedo,
  faPen,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import axios from "axios";
import { useDocument } from "react-firebase-hooks/firestore";

import PollFields from "./PollFields";
import Poll from "./Poll";
import {
  INVITE_PROCESS,
  API_BASE_URL,
  ADMIN_GET_APP_LIST,
  PROD_HOSTED_API_BASE_URL
} from "../../../../config";
import { postData } from "../../../../utils";
import { auth, firestore } from "../../../../firebase";
import UserProfile from "../UserProfile";

function Polls(props) {
  // CONSTANTS
  const user = auth.currentUser;
  const { eventId, currentEvent } = props;
  const [eventSnapshot, loadingEvent, errorEvent] = useDocument(
    firestore.collection("events").doc(eventId)
  );

  // STATES
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roomLinked, setRoomLinked] = useState(false);
  const [items, setItems] = useState([]);
  const [fetchingItems, setFetchingItems] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetails, setShowItemDetails] = useState(false);
  const [filter, setFilter] = useState("");
  const [showMoreDetailsPanel, setShowMoreDetailsPanel] = useState(false);
  const [selectedMoreDetails, setSelectedMoreDetails] = useState(null);

  const processRoomInvite = (inviteProcessData) => {
    axios
      .post(`https://${PROD_HOSTED_API_BASE_URL}${INVITE_PROCESS}`, inviteProcessData)
      .then((response) => {
        console.log("Invite processing: ", response?.response?.data?.message);
      })
      .catch((error) => {
        console.error(
          "Error processing invite: ",
          error?.response?.data?.message
        );
      });
  };

  const handleFetchItems = async () => {
    try {
      const response = await postData(`${API_BASE_URL}${ADMIN_GET_APP_LIST}`, {
        event_id: eventId,
        user_id: auth.currentUser.uid,
        types: ["poll"],
      });

      if (response.status === 200) {
        const { data } = response;

        setItems(data.pollList);
        setFetchingItems(false);
      } else {
        setFetchingItems(false);
      }
    } catch (error) {
      console.log("[Error] Fetching polls: ", error);
      setFetchingItems(false);
    }
  };

  const handleOpenItem = (resource) => {
    setShowItemDetails(true);
    setSelectedItem(resource);
    handleCloseAddItem();
    setShowMoreDetailsPanel(false);
    setSelectedMoreDetails(null);
  };

  const handleCloseItem = () => {
    setShowItemDetails(false);
    setSelectedItem(null);
    setShowMoreDetailsPanel(false);
    setSelectedMoreDetails(null);
  };

  const handleCloseMoreDetails = () => {
    setShowMoreDetailsPanel(false);
    setSelectedMoreDetails(null);
  };

  const handleOpenAddItem = () => {
    setShow(true);
    handleCloseItem();
  };

  const handleCloseAddItem = () => {
    setShow(false);
  };

  useEffect(() => {
    if (
      currentEvent?.workspace_id &&
      currentEvent?.room_id &&
      currentEvent?.room_invite_code
    ) {
      setRoomLinked(true);
      setLoading(false);

      const inviteProcessData = {
        invite_code: currentEvent?.room_invite_code,
        user_id: user?.uid,
      };
      processRoomInvite(inviteProcessData);
    } else {
      setLoading(false);
    }

    // Fetch: Polls
    handleFetchItems();
  }, []);

  const handleRefresh = () => {
    // Fetch: Polls
    handleFetchItems();
  };

  if (loading)
    return (
      <div className="d-flex align-items-center justify-content-center text-white p-3">
        <Spinner animation="border" size="sm" className="mr-2" /> Loading...
      </div>
    );

  if (roomLinked)
    return (
      <div className="p-3" style={{ height: "100vh", overflowY: "auto" }}>
        <div className="row">
          <div className="col-md-12 mb-3">
            <h4 className="text-white">Polls</h4>
          </div>

          <div className="col-md-12 mb-3">
            <Card
              style={{ width: "100%" }}
              className="shadow-sm bg-dark shadow-sm mb-3 text-white"
            >
              <Card.Header>
                <Card.Title className="mb-0">Settings</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <FontAwesomeIcon icon={faTasks} className="mx-4 btn-hero" />
                  <div className="d-flex flex-column">
                    <span className="text-white small font-weight-bold text-uppercase">
                      Show Polls button
                    </span>
                    <span>
                      <FormCheck
                        type="switch"
                        id="showPolls"
                        label={
                          eventSnapshot?.data()?.poll_enabled ? "On" : "Off"
                        }
                        checked={
                          eventSnapshot?.data()?.poll_enabled ? true : false
                        }
                        onChange={(e) =>
                          firestore.collection("events").doc(eventId).update({
                            poll_enabled: e.target.checked,
                          })
                        }
                      ></FormCheck>
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          <div
            className={
              showItemDetails || show
                ? showMoreDetailsPanel
                  ? "col-md-4"
                  : "col-md-6"
                : "col-md-12"
            }
          >
            <div className="text-white border border-secondary">
              <div className="d-flex align-items-end justify-content-between px-3 py-2">
                <div className="d-flex flex-column justify-content-start">
                  <Dropdown className="w-100">
                    <Dropdown.Toggle
                      variant="light"
                      id="dropdown-basic"
                      size="sm"
                    >
                      <span className="text-capitalize">
                        {filter === "" ? (
                          <span>
                            <FontAwesomeIcon
                              icon={faThLarge}
                              className="mr-2"
                            />
                            View all
                          </span>
                        ) : (
                          <span>
                            <FontAwesomeIcon
                              icon={faThLarge}
                              className="mr-2"
                            />
                            View all
                          </span>
                        )}
                      </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item
                        style={
                          filter === ""
                            ? { backgroundColor: "rgba(51,51,51,0.25)" }
                            : {}
                        }
                        onClick={() => setFilter("")}
                      >
                        <FontAwesomeIcon icon={faThLarge} className="mr-2" />
                        View all
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className="d-flex align-items-end">
                  {fetchingItems && (
                    <div className="mr-3">
                      <p className="mb-0">Fetching...</p>
                    </div>
                  )}
                  <Button
                    variant="light"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={fetchingItems}
                    className="mr-3"
                  >
                    {fetchingItems ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faRedo} />
                    )}{" "}
                    Refresh
                  </Button>
                  <Button
                    size="sm"
                    className="text-uppercase"
                    onClick={handleOpenAddItem}
                  >
                    Add Poll
                  </Button>
                </div>
              </div>
              <table class="table table-borderless text-white">
                <thead className="border-top border-secondary">
                  <tr className="">
                    <th className="py-1 font-weight-bold small">#</th>
                    <th className="py-1 font-weight-bold small">Name</th>
                    <th className="py-1 font-weight-bold small">Deadline</th>
                    <th className="py-1 font-weight-bold small text-center">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items
                    ?.filter((e) => {
                      if (filter === "") return true;
                      else {
                        return true;
                      }
                    })
                    ?.map((item, key) => (
                      <tr
                        className="border-top border-secondary align-items-center pointer"
                        key={key}
                        onClick={() => handleOpenItem(item)}
                        style={
                          selectedItem?.id === item.id
                            ? {
                                fontSize: "90%",
                                backgroundColor: "rgba(200, 200, 200, 0.15)",
                              }
                            : { fontSize: "90%" }
                        }
                      >
                        <td>{key + 1}</td>
                        <td>{item?.title}</td>
                        <td>{moment(item?.deadline).fromNow()}</td>
                        <td className="text-center">
                          {moment(item?.created_at).format("LLL")}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section: Show selected Item */}
          {showItemDetails && selectedItem && (
            <div
              className={
                showMoreDetailsPanel
                  ? "col-md-4 p-0 m-0 h-100"
                  : "col-md-6 p-0 m-0 h-100"
              }
            >
              <Poll
                handleCloseItem={handleCloseItem}
                setShowMoreDetailsPanel={setShowMoreDetailsPanel}
                showMoreDetailsPanel={showMoreDetailsPanel}
                setSelectedMoreDetails={setSelectedMoreDetails}
                selectedMoreDetails={selectedMoreDetails}
                className="text-white"
                data={{
                  contentId: selectedItem.content_id,
                  id: selectedItem.id,
                  ...selectedItem,
                }}
              />
            </div>
          )}

          {/* Section: Show More Details */}
          {showItemDetails &&
            selectedItem &&
            showMoreDetailsPanel &&
            selectedMoreDetails && (
              <div className="col-md-4 p-0 m-0 h-100">
                <div className="border border-secondary text-white mr-3">
                  <div className="p-3 d-flex align-items-center border-bottom border-secondary">
                    <UserProfile
                      userDetails={selectedMoreDetails?.response?.user_details}
                      imgSize={40}
                      subText={moment(
                        selectedMoreDetails?.response?.responseTime
                      ).format("LLL")}
                      className="flex-grow-1"
                    />
                    <div>
                      <a
                        className="text-white pointer p-2"
                        onClick={handleCloseMoreDetails}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </a>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="mb-0">
                      <b>Response: </b>
                      {selectedMoreDetails?.response?.optionValue}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Section: Add Item */}
          {show && (
            <div className="col-md-6 p-0 m-0 h-100">
              <div className="border border-secondary text-white mr-3">
                <div className="p-3 d-flex align-items-center border-bottom border-secondary">
                  <h5 className="flex-grow-1 mb-0">Add Poll</h5>
                  <a
                    className="text-white pointer p-2"
                    onClick={handleCloseAddItem}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </a>
                </div>
                <div className="p-1">
                  <PollFields onHide={handleCloseAddItem} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Add Poll</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0 m-0">
            <PollFields onHide={handleClose} />
          </Modal.Body>
        </Modal> */}
      </div>
    );
  else {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <p className="text-white p-4">
          To request access of Poll app, please email us at{" "}
          <a href="mailto:hello@igesia.co" target="_blank">
            hello@igesia.co
          </a>
        </p>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  eventId: state.eventId,
  currentEvent: state.currentEvent,
});

export default connect(mapStateToProps)(Polls);
